/**
 * Phone number sign-in (docs/features/account-sign-in.md → "Phone number"): the rules the clients share. The server
 * parses, checks and masks numbers (libphonenumber); here are the country picker's dial codes and flags, the resend
 * countdown, and what each server error says.
 */

/** Dial codes for the countries we can text (AccountResponse.phoneCountries; unknown ones fall back to the code). */
export const DIAL_CODES: Readonly<Record<string, string>> = {
  US: "+1",
  CA: "+1",
  BR: "+55",
  PT: "+351",
  GB: "+44",
  AR: "+54",
  MX: "+52",
  ES: "+34",
};

/** The countries shown when the server didn't send any. */
export const DEFAULT_COUNTRIES: readonly string[] = ["US", "CA", "BR", "PT", "GB", "AR", "MX", "ES"];

export const dialCode = (country: string): string => DIAL_CODES[country.toUpperCase()] ?? "";

/** "🇧🇷" for "BR" (regional indicator symbols). */
export function flagOf(country: string): string {
  const cc = country.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return "";
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/**
 * The country the picker starts on: the imported number's, else the locale's region when we can text it, else the
 * first one offered.
 */
export function defaultCountry(countries: readonly string[], locale: string | undefined, imported?: string): string {
  const list = countries.length > 0 ? countries : DEFAULT_COUNTRIES;
  if (imported !== undefined && list.includes(imported)) return imported;
  const region = /[-_]([A-Za-z]{2})\b/.exec(locale ?? "")?.[1]?.toUpperCase();
  if (region !== undefined && list.includes(region)) return region;
  if (locale?.toLowerCase().startsWith("pt") && list.includes("BR")) return "BR";
  return list[0] ?? "US";
}

/** Countries matching a picker search (name or dial code), in the given order. */
export function searchCountries(countries: readonly string[], query: string, nameOf: (cc: string) => string): string[] {
  const q = query.trim().toLowerCase().replace(/^\+/, "");
  if (q === "") return [...countries];
  return countries.filter((cc) => nameOf(cc).toLowerCase().includes(q) || dialCode(cc).slice(1).startsWith(q) || cc.toLowerCase() === q);
}

/** Only what a phone number can hold (digits, spaces, + ( ) - .), at most 32 characters. */
export const cleanNumber = (input: string): string => input.replace(/[^\d+()\-.\s]/g, "").slice(0, 32);

/** Enough digits to send (the server decides whether the number is real). */
export const isNumberComplete = (input: string): boolean => input.replace(/\D/g, "").length >= 6;

/** 6 digits from whatever was typed or pasted. */
export const normalizePhoneCode = (input: string): string => input.replace(/\D/g, "").slice(0, 6);

/** Seconds until another code can be asked for (0 = now). */
export const resendLeft = (resendAt: number | undefined, now: number): number => (resendAt === undefined ? 0 : Math.max(0, Math.ceil((resendAt - now) / 1000)));

/** "0:24" for the resend countdown. */
export function countdown(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** What a failed phone call says (phone.error.<PhoneError>); undefined = not a phone error (proof, network, …). */
export type PhoneError = "invalidCode" | "tooMany" | "inUse" | "invalidPhone" | "country" | "usNotReady" | "caNotReady" | "rateLimited" | "notSent" | "required" | "alreadyUsed" | "wait";

export function phoneErrorFor(code: string | null | undefined): PhoneError | undefined {
  switch ((code ?? "").toLowerCase()) {
    case "invalid_phone":
      return "invalidPhone";
    case "country_not_supported":
      return "country";
    // US texts wait for the toll-free number's registration.
    case "us_not_ready":
      return "usNotReady";
    case "ca_not_ready":
      return "caNotReady";
    case "phone_in_use":
      return "inUse";
    case "invalid_code":
      return "invalidCode";
    case "too_many_attempts":
      return "tooMany";
    case "rate_limited":
      return "rateLimited";
    case "resend_wait":
      return "wait";
    case "sms_not_sent":
    case "phone_unavailable":
    case "voice_unavailable":
      return "notSent";
    case "phone_required":
      return "required";
    case "phone_already_used":
      return "alreadyUsed";
    default:
      return undefined;
  }
}
